import { useFormik } from 'formik';
import { InferType } from 'yup';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { companyValidationSchema } from 'src/utils/validationScheme.utils';
import { SelectChangeEvent } from '@mui/material';
import TextField from 'src/components/InputFields/TextField';
import ImageUpload from 'src/components/InputFields/ImageUpload';
import { companyProfileInitialValues } from 'src/utils/initialValues.utils';
import Cookies from 'js-cookie';
import {
  apiUrls,
  BYTES_PER_MB,
  companySettings,
  dataStorageKeywords,
  errorMessages,
  fileSize,
  help,
  successMessages,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import CountryPicker from 'src/components/InputFields/CountryPicker';
import CustomBox from 'src/components/StyledComponents/StyledBox';
import {
  StyledSettingsContainer,
  StyledFormContainer,
  StyledSaveButton,
} from 'src/components/StyledComponents/StyledSettingsTab';
import { calculateBoxHeight, formatAssetUrl } from 'src/utils/company.utils';
import { FieldType } from 'src/models/enums/FieldType.enum';
import { getChangedFields } from 'src/utils/function.utils';

export const CompanyTab: FC = () => {
  const [imagePreview, setImagePreview] = useState<string | ArrayBuffer | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isValueChanged, setIsValueChanged] = useState(false);
  const [fetchedValues, setFetchedValues] = useState(
    companyProfileInitialValues
  );
  const [isLoading, setIsLoading] = useState(true);
  const { showSnackbar } = StyledSnackbar();
  const [logo, setLogo] = useState<string | null>(null);

  const handleSubmit = async () => {
    try {
      const castedValues = companyValidationSchema.cast(
        formik.values
      ) as InferType<typeof companyValidationSchema>;
      const payload = getChangedFields(castedValues, fetchedValues);

      const response = await axiosTokenInstance.put(
        apiUrls.UPDATE_COMPANY_INFO,
        payload
      );

      const updatedData = {
        companyName: response.data.company.name,
        companyRegNo: response.data.company.registrationNo,
        country: response.data.company.country,
        addressLine1: response.data.company.addressLine1 || '',
        addressLine2: response.data.company.addressLine2 || '',
        postalCode: response.data.company.postalCode || '',
        city: response.data.company.city || '',
        telephoneNumber: response.data.company.telephoneNumber || '',
        websiteUrl: response.data.company.websiteUrl || '',
        email: response.data.company.email || '',
      };

      formik.setValues(updatedData);
      setFetchedValues(updatedData);
      showSnackbar(response.data.message, 'success');
      setIsValueChanged(false);
    } catch (error: any) {
      if (
        error.response?.status === 400 ||
        error.response?.status === 403 ||
        error.response?.status === 409
      ) {
        showSnackbar(error.response.data.message, 'error');
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    }
  };

  const loadCompanyInfo = async () => {
    setIsLoading(true);

    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_COMPANY_INFO);
      const res = response.data;

      const logoPath =
        res.logo ||
        `${companySettings.LOGO_PATH_PREFIX}${companySettings.DEFAULT_LOGO}`;

      const mappedValues = {
        companyName: res.name,
        companyRegNo: res.registrationNo,
        country: res.country,
        addressLine1: res.addressLine1 || '',
        addressLine2: res.addressLine2 || '',
        postalCode: res.postalCode || '',
        city: res.city || '',
        telephoneNumber: res.telephoneNumber || '',
        websiteUrl: res.websiteUrl || '',
        email: res.email || '',
      };

      formik.setValues(mappedValues);
      setFetchedValues(mappedValues);

      setLogo(formatAssetUrl(logoPath));
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: companyProfileInitialValues,
    validationSchema: companyValidationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const changedFields = getChangedFields(formik.values, fetchedValues);
      setIsValueChanged(Object.keys(changedFields).length > 0);
    }
  }, [formik.values, isLoading, fetchedValues]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileSizeInMB = file.size / BYTES_PER_MB;
      if (fileSizeInMB > fileSize.MAX_MB) {
        showSnackbar(help.FILE_SIZE_BIG, 'error');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      await handleImageUpload(file);
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await axiosTokenInstance.put(
        apiUrls.UPLOAD_LOGO,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      showSnackbar(successMessages.COMPANY_LOGO_UPDATED, 'success');
      setLogo(response.data.logoUrl);
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    formik.setFieldValue('country', value);
  };

  return (
    <StyledSettingsContainer>
      <CustomBox
        height="220px"
        marginBottom={2}
        title="Company Logo"
        width="auto"
      >
        <ImageUpload
          imagePreview={imagePreview || logo}
          fileInputRef={fileInputRef}
          onFileChange={handleFileChange}
          onClick={handleUploadClick}
          buttonWidth="135px"
        />
      </CustomBox>

      <CustomBox
        height={'auto'}
        marginBottom={2}
        title="Company Information"
        width="auto"
      >
        <StyledFormContainer
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <TextField
            id="companyName"
            label="Company Name"
            type="edit"
            formikProps={formik}
          />
          <TextField
            id="companyRegNo"
            label="Company Registration Number"
            type="edit"
            formikProps={formik}
          />

          <TextField
            id="addressLine1"
            label="Address Line 1"
            type="edit"
            formikProps={formik}
          />

          <TextField
            id="addressLine2"
            label="Address Line 2"
            type="edit"
            formikProps={formik}
          />

          <TextField
            id="postalCode"
            label="Postal Code"
            type="edit"
            formikProps={formik}
          />

          <TextField id="city" label="City" type="edit" formikProps={formik} />

          <CountryPicker
            label="Country"
            value={formik.values.country}
            onChange={handleCountryChange}
            type={FieldType.EDIT}
            sx={{
              marginBottom: 3,
            }}
          />

          <TextField
            id="telephoneNumber"
            label="Telephone Number"
            type="edit"
            formikProps={formik}
          />

          <TextField
            id="websiteUrl"
            label="Website URL"
            type="edit"
            formikProps={formik}
          />

          <StyledSaveButton
            type="submit"
            isActive={isValueChanged}
            disabled={!isValueChanged}
          >
            Save
          </StyledSaveButton>
        </StyledFormContainer>
      </CustomBox>
    </StyledSettingsContainer>
  );
};
